import AppColors from 'general/constants/AppColors';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import ChargeHelper from 'features/Charge/chargeHelper';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import imgEmptyNoData from 'assets/images/Dialogs/img_no_firmware.svg';

FirmwareHistoryView.propTypes = {
  item: PropTypes.object,
  pointId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onDismiss: PropTypes.func,
};

function FirmwareHistoryView({ item, pointId, onDismiss }) {
  const [historyList, setHistoryList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!pointId) return;
    setLoading(true);
    ChargeHelper.API.requestGetFirmwareHistory(pointId)
      .then((res) => setHistoryList(res?.data ?? res ?? []))
      .finally(() => setLoading(false));
  }, [pointId]);

  const currentVersion = item?.firmwareVersion ?? item?.firmware ?? item?.currentFirmware ?? '—';

  const renderStatusBadge = (histItem) => {
    const status = histItem?.status;
    const progress = histItem?.progress ?? histItem?.percentage;

    if (status === 'InProgress' || status === 'Downloading' || status === 'Installing') {
      return (
        <div className="d-flex align-items-center gap-2">
          <span style={UIHelper.buildFontStyle('400', '13px', AppColors.gray[600])}>Trạng thái</span>
          <div className="d-flex align-items-center gap-1">
            <i className="fa-solid fa-circle-notch fa-spin" style={{ fontSize: 13, color: AppColors.primary }} />
            <span style={UIHelper.buildFontStyle('500', '13px', AppColors.primary)}>
              Đang cập nhật{progress != null ? ` (${progress}%)` : ''}
            </span>
          </div>
        </div>
      );
    }

    if (status === 'Success' || status === 'Installed') {
      return (
        <div className="d-flex align-items-center gap-2">
          <span style={UIHelper.buildFontStyle('400', '13px', AppColors.gray[600])}>Trạng thái</span>
          <span style={{
            ...UIHelper.buildFontStyle('500', '12px', AppColors.primary),
            background: 'rgba(48,189,111,0.12)',
            borderRadius: 20,
            padding: '2px 14px',
          }}>Thành công</span>
        </div>
      );
    }

    if (status === 'Failed' || status === 'Error') {
      return (
        <div className="d-flex align-items-center gap-2">
          <span style={UIHelper.buildFontStyle('400', '13px', AppColors.gray[600])}>Trạng thái</span>
          <span style={{
            ...UIHelper.buildFontStyle('500', '12px', '#E74C3C'),
            background: 'rgba(231,76,60,0.1)',
            borderRadius: 20,
            padding: '2px 14px',
          }}>Không thành công</span>
        </div>
      );
    }

    if (status) {
      return (
        <div className="d-flex align-items-center gap-2">
          <span style={UIHelper.buildFontStyle('400', '13px', AppColors.gray[600])}>Trạng thái</span>
          <span style={UIHelper.buildFontStyle('400', '13px', AppColors.gray[700])}>{status}</span>
        </div>
      );
    }

    return null;
  };

  return (
    <div>
      {/* header */}
      <div
        className="d-flex align-items-center justify-content-between px-4 py-3"
        style={{ borderBottom: `1px solid ${AppColors.gray[200]}` }}
      >
        <span style={UIHelper.buildFontStyle('700', '20px', AppColors.gray[800])}>
          Lịch sử cập nhật firmware
        </span>
        <button
          onClick={onDismiss}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: AppColors.gray[500] }}
        >
          <i className="fa-regular fa-xmark" />
        </button>
      </div>

      {/* current base version */}
      <div className="px-4 pt-3 pb-2" style={{ borderBottom: `1px solid ${AppColors.gray[100]}` }}>
        <div className="d-flex align-items-center justify-content-between">
          <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>{currentVersion}</span>
          <span style={{
            ...UIHelper.buildFontStyle('600', '13px', '#2563EB'),
            background: '#F2F5F9',
            borderRadius: 6,
            padding: '3px 12px',
          }}>Bản gốc</span>
        </div>
        <span style={UIHelper.buildFontStyle('400', '12px', AppColors.gray[500])}>
          {Utils.formatDateTimeToString(item?.createdAt ?? item?.updatedAt) ?? '—'}
        </span>
      </div>

      {/* loading */}
      {loading && (
        <div className="d-flex justify-content-center py-5">
          <span className="spinner-border spinner-border-sm text-success" />
        </div>
      )}

      {/* empty state */}
      {!loading && !historyList.length && (
        <div className="d-flex flex-column align-items-center justify-content-center text-center px-5 py-5 gap-2">
          <img src={imgEmptyNoData} alt="" style={{ width: 90, height: 90, objectFit: 'contain', opacity: 0.7 }} />
          <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[700])}>
            Chưa có lịch sử cập nhật
          </span>
          <span style={UIHelper.buildFontStyle('400', '13px', AppColors.gray[500])}>
            Thiết bị đang dùng firmware khai báo ban đầu. Lịch sử sẽ xuất hiện sau lần cập nhật đầu tiên.
          </span>
        </div>
      )}

      {/* history list */}
      {!loading && historyList.length > 0 && (
        <div style={{ maxHeight: 380, overflowY: 'auto' }}>
          {historyList.map((histItem, index) => (
            <div key={index}>
              <div className="px-4 py-3">
                {/* from → to version row */}
                <div className="d-flex align-items-start gap-2 mb-2">
                  <div className="d-flex flex-column" style={{ flex: 1 }}>
                    <span style={UIHelper.buildFontStyle('600', '13px', AppColors.gray[800])}>
                      {histItem?.fromVersion ?? histItem?.currentVersion ?? '—'}
                    </span>
                    <span style={UIHelper.buildFontStyle('400', '12px', AppColors.gray[500])}>
                      {Utils.formatDateTimeToString(histItem?.fromDate ?? histItem?.createdAt) ?? '—'}
                    </span>
                  </div>
                  <i
                    className="fa-regular fa-arrow-right"
                    style={{ color: AppColors.gray[400], fontSize: 13, marginTop: 3, flexShrink: 0 }}
                  />
                  <div className="d-flex flex-column" style={{ flex: 1, alignItems: 'flex-end' }}>
                    <span style={UIHelper.buildFontStyle('600', '13px', AppColors.gray[800])}>
                      {histItem?.toVersion ?? histItem?.newVersion ?? histItem?.version ?? '—'}
                    </span>
                    <span style={UIHelper.buildFontStyle('400', '12px', AppColors.gray[500])}>
                      {histItem?.toDate ? Utils.formatDateTimeToString(histItem.toDate) : '-/-'}
                    </span>
                  </div>
                </div>
                {/* status */}
                {renderStatusBadge(histItem)}
              </div>
              {index < historyList.length - 1 && (
                <div style={{ borderBottom: `1px dashed ${AppColors.gray[200]}` }} />
              )}
            </div>
          ))}
        </div>
      )}

      {/* footer */}
      <div
        className="d-flex justify-content-end px-4 py-3"
        style={{ borderTop: `1px solid ${AppColors.gray[200]}` }}
      >
        {UIHelper.buildNegativeButton('Bỏ qua', onDismiss)}
      </div>
    </div>
  );
}

export default FirmwareHistoryView;
